import { useEffect, useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/daetn0htto0ceqqnh3r0';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export function SubmitSection() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  const [textLen, setTextLen] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = String(fd.get('contact_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      setTextLen(0);
      return;
    }
    fd.delete('contact_alt');

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText || '');
      } catch {
        parsed = {};
      }
      const msg = [parsed?.meta?.message, parsed?.meta?.detail, responseText]
        .filter(Boolean)
        .join(' ');
      const ok = res.ok && parsed?.code === 'OK' && !/spam/i.test(responseText + msg);
      if (ok) {
        setStatus('success');
        form.reset();
        setTextLen(0);
      } else {
        setStatus('error');
        setFormError(msg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络出了点小问题，请检查后重试。');
    }
  };

  return (
    <section id="submit" className="py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 rounded-3xl bg-background-100/80 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="text-sm font-semibold text-primary-500">SUBMIT · 投稿征集</span>
            <h2 className="mt-2 font-display text-3xl text-foreground-950 md:text-4xl">
              有好瓜，端上来一起吃
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              如果你刷到了有意思的热点、发现了值得深挖的好瓜，或者单纯想聊聊你的看法，都可以在下方留下线索。一经采纳并审核通过，我们会整理成内容分享给更多瓜友。
            </p>
            <ul className="mt-6 flex flex-col gap-3">
              {[
                { icon: 'ri-focus-3-line', text: '内容真实可查，请尽量附上信息来源' },
                { icon: 'ri-flag-line', text: '拒绝恶意造谣，理性吃瓜更长久' },
                { icon: 'ri-timer-line', text: '我们会在 24 小时内认真阅读每份投稿' },
              ].map((p) => (
                <li key={p.text} className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>
          </div>

          <form
            data-readdy-form="chigua-submit"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col">
                <label htmlFor="nick" className="mb-1.5 text-sm font-semibold text-foreground-800">
                  你的称呼
                </label>
                <input
                  id="nick"
                  name="nick"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="email" className="mb-1.5 text-sm font-semibold text-foreground-800">
                  联系邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="方便我们回复你"
                  className="rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <label htmlFor="category" className="mb-1.5 text-sm font-semibold text-foreground-800">
                属于哪一类瓜
              </label>
              <select
                id="category"
                name="category"
                className="rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              >
                <option>明星八卦</option>
                <option>影视热剧</option>
                <option>综艺现场</option>
                <option>音乐创作</option>
                <option>电竞电竞</option>
                <option>社会暖闻</option>
              </select>
            </div>

            <div className="flex flex-col">
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="content" className="text-sm font-semibold text-foreground-800">
                  瓜料线索 / 想说的
                </label>
                <span className="text-xs text-foreground-400">{textLen} / 500</span>
              </div>
              <textarea
                id="content"
                name="content"
                rows={4}
                required
                maxLength={500}
                onChange={(e) => setTextLen(e.target.value.length)}
                placeholder="简单说说发生了什么，有哪些细节或信息来源……"
                className="resize-none rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>
            </div>

            <input
              type="text"
              name="contact_alt"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            {status === 'success' && (
              <p className="rounded-lg bg-secondary-100 px-3 py-2.5 text-sm font-medium text-secondary-800">
                <i className="ri-checkbox-circle-fill mr-1"></i>
                收到啦！感谢你的投稿，我们会在 24 小时内认真查看，边吃边看感谢有你。
              </p>
            )}
            {status === 'error' && (
              <p className="rounded-lg bg-primary-100 px-3 py-2.5 text-sm font-medium text-primary-700">
                <i className="ri-error-warning-fill mr-1"></i>
                {formError || '提交失败，请稍后再试。'}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-1 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 py-3 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
            >
              {status === 'sending' ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 提交中
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill"></i> 提交投稿
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export function BackTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label="返回顶部"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-xl text-white shadow-lg transition-all hover:bg-primary-600 ${
        show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
      }`}
    >
      <i className="ri-arrow-up-line"></i>
    </button>
  );
}